import React, { useEffect, useState } from "react";
import { Box, Button, TextField, MenuItem, CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";

import {
  GetInterviewConfig,
  InterviewConfig as createInterviewConfig,
  UpdateInterviewConfig,
} from "../../../services/interviewService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import tokens from "../../theme/tokens";

const { color, font, radius } = tokens;

/** Option sets carried over verbatim from the old InterviewConfig page. */
const FIELDS = [
  {
    key: "interview_voice_type",
    label: "Voice type",
    hint: "The voice the AI interviewer speaks with.",
    options: [
      ["male", "Male"],
      ["female", "Female"],
    ],
  },
  {
    key: "voice_id",
    label: "Voice variant",
    hint: "Which specific voice to use.",
    options: [
      ["1", "Voice 1"],
      ["2", "Voice 2"],
      ["3", "Voice 3"],
    ],
  },
  {
    key: "interview_style",
    label: "Style",
    hint: "Tone and approach of the interview.",
    options: [
      ["formal", "Formal & Structured"],
      ["friendly", "Friendly & Conversational"],
      ["casual", "Casual & Relaxed"],
      ["empathetic", "Empathetic & Supportive"],
    ],
  },
  {
    key: "interview_duration",
    label: "Duration",
    hint: "Roughly how long the interview should run.",
    options: [
      ["short", "Short (10-15 minutes)"],
      ["medium", "Medium (20-30 minutes)"],
      ["long", "Long (40-50 minutes)"],
    ],
  },
  {
    key: "interview_interaction_mode",
    label: "Interaction mode",
    hint: "How the AI moves through its questions.",
    options: [
      ["open-ended", "Open-ended"],
      ["follow-up-driven", "Follow-up driven"],
    ],
  },
];

const EMPTY = Object.fromEntries(FIELDS.map((f) => [f.key, ""]));

const field = {
  "& .MuiOutlinedInput-root": { borderRadius: `${radius.control}px` },
  "& .MuiOutlinedInput-input": { fontSize: 13.5 },
};

/**
 * The AI interviewer's configuration — voice, style, length, question strategy.
 * Was its own route (/dashboard/interview-config/:jobId); the revamp folds it into
 * Job settings, since it is a property of the job, not a place you navigate to.
 */
const InterviewConfigForm = ({ jobId, onClose }) => {
  const dispatch = useDispatch();
  const [config, setConfig] = useState(EMPTY);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setLoading(true);
    GetInterviewConfig(jobId)
      .then((res) => {
        if (cancelled) return;
        // No config yet is the normal first-run state, not an error.
        if (res && Object.keys(res).length) {
          setConfig({ ...EMPTY, ...res });
          setExists(true);
        }
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const complete = FIELDS.every((f) => config[f.key]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(FIELDS.map((f) => [f.key, config[f.key]]));
      if (exists) {
        await UpdateInterviewConfig(payload, jobId);
      } else {
        await createInterviewConfig(payload, jobId);
        setExists(true);
      }
      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: "Interview configuration saved.",
        })
      );
      onClose?.();
    } catch (e) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: e.message || "Failed to save interview configuration.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ font: `400 12px/1.6 ${font.sans}`, color: color.textMuted, mb: 2.5 }}>
        How the AI conducts interviews for this job.
      </Box>

      {FIELDS.map((f) => (
        <Box key={f.key} sx={{ mb: 2 }}>
          <Box sx={{ mb: 0.75, font: `500 12px ${font.sans}`, color: color.textSecondary }}>
            {f.label}
          </Box>
          <TextField
            select
            fullWidth
            size="small"
            value={config[f.key]}
            onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
            sx={field}
          >
            {f.options.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ mt: 0.5, font: `400 11px ${font.sans}`, color: color.textMuted }}>
            {f.hint}
          </Box>
        </Box>
      ))}

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving || !complete}
        sx={{ mt: 1 }}
      >
        {saving ? "Saving…" : exists ? "Update configuration" : "Save configuration"}
      </Button>
      {!complete && (
        <Box sx={{ mt: 1, font: `400 11px ${font.sans}`, color: color.textMuted }}>
          All five settings are required before the AI can run an interview.
        </Box>
      )}
    </>
  );
};

export default InterviewConfigForm;
